import React, { useState, useEffect } from 'react';
import {
  List, ListItem, ListItemText, Drawer, Box, Avatar, Typography,
  IconButton, useMediaQuery, AppBar, Toolbar, Divider
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt, faBullhorn, faLifeRing,
  faUserCog, faSignOutAlt, faCreditCard, faBars, faXmark
} from '@fortawesome/free-solid-svg-icons';
import SnackbarAlert from './SnackbarAlert'; // Assuming this is correct
import { toast } from 'react-toastify'; // Assuming this is correct
import baseURL from '../config';
import COLORS from '../../../../constants/colors';
// Import your PNG logo and icon here
import YambaFullLogoPng from '../../../../assets/logo.png'; // Adjust this path for your PNG logo!


const MS_COLORS = {
  primary: '#2D4A3D',
  primaryLight: '#EBF4FD',
  text: '#1a1a2e',
  textSecondary: '#8a8f9d',
  background: '#FFFFFF',
  backgroundLight: '#F8F9FB',
  border: '#eaecf0',
  hoverBackground: '#F3F5F8',
  white: '#FFFFFF',
  accent: '#107C10',
  accentDark: '#0A640A',
};

const Navigation = () => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [user, setUser] = useState(null);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const location = useLocation(); // for active link detection

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${baseURL}/user/profile`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch user data');

      const userData = await response.json();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const toggleDrawer = () => setDrawerOpen(!isDrawerOpen);

  const handleLogout = async () => {
    try {
      const response = await fetch(`${baseURL}/auth/user/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Logout failed. Please try again.');

      toast.success('Logged out successfully!');

      setTimeout(() => {
        window.location.href = '/user/login';
      }, 2000);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed. Try again.');
    }
  };

  // Function to check if a route is active
  const isActive = (path) => location.pathname === path;

  const drawerContent = (
    <Box
      sx={{
        width: isMobile ? 250 : 270,
        backgroundColor: MS_COLORS.background,
        color: MS_COLORS.text,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Brand header */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <img src={YambaFullLogoPng} alt="Yamba Logo" style={{ height: '32px' }} />
        </Box>

        {/* Profile Section */}
        <Box display="flex" alignItems="center" sx={{ p: 1.5, borderRadius: '12px', backgroundColor: MS_COLORS.backgroundLight, border: `1px solid ${MS_COLORS.border}` }}>
          <Avatar
            alt={user?.name || 'User'}
            src={user?.profileImage || '/default-avatar.png'}
            sx={{ width: 38, height: 38, mr: 1.5, fontSize: '0.9rem' }}
          />
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: MS_COLORS.text, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name || 'Guest'}
            </Typography>
            <Typography variant="caption" sx={{ color: MS_COLORS.textSecondary, fontSize: '0.72rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              {user?.email || 'Not Logged In'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: MS_COLORS.border, mx: 2 }} />

      {/* Nav List */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        <List disablePadding>
          {[
            { to: '/user/dashboard', label: 'Dashboard', icon: faTachometerAlt },
            { to: '/user/campaigns', label: 'My Campaigns', icon: faBullhorn },
            { to: '/user/transactions', label: 'Transactions', icon: faCreditCard },
            { to: '/user/help', label: 'Help Center', icon: faLifeRing },
            { to: '/user/account', label: 'Account', icon: faUserCog },
            { label: 'Logout', icon: faSignOutAlt, action: handleLogout },
          ].map((item, index) => {
            const isSelected = item.to && isActive(item.to);
            const listItemStyle = {
              cursor: 'pointer',
              borderRadius: '10px',
              backgroundColor: isSelected ? MS_COLORS.primaryLight : 'transparent',
              borderLeft: isSelected ? `3px solid ${MS_COLORS.primary}` : '3px solid transparent',
              transition: 'all 0.18s ease',
              textDecoration: 'none',
              color: isSelected ? MS_COLORS.primary : MS_COLORS.text,
              display: 'flex',
              alignItems: 'center',
              mb: 0.5,
              px: 1.5,
              py: 1.1,
              '&:hover': {
                backgroundColor: MS_COLORS.hoverBackground,
                color: MS_COLORS.primary,
                borderLeft: `3px solid ${MS_COLORS.primary}40`,
                '& .nav-icon': { color: MS_COLORS.primary },
              },
            };

            if (item.to) {
              return (
                <ListItem
                  key={index}
                  component={NavLink}
                  to={item.to}
                  onClick={() => isMobile && setDrawerOpen(false)}
                  sx={listItemStyle}
                  exact="true"
                >
                  <Box className="nav-icon" sx={{ mr: 1.5, color: isSelected ? MS_COLORS.primary : MS_COLORS.textSecondary, fontSize: '15px', width: 18, textAlign: 'center', transition: 'color 0.18s ease' }}>
                    <FontAwesomeIcon icon={item.icon} />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 500, fontSize: '0.9rem', lineHeight: 1 }}>
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            } else {
              return (
                <ListItem
                  key={index}
                  onClick={() => { item.action(); if (isMobile) setDrawerOpen(false); }}
                  sx={listItemStyle}
                >
                  <Box className="nav-icon" sx={{ mr: 1.5, color: MS_COLORS.textSecondary, fontSize: '15px', width: 18, textAlign: 'center' }}>
                    <FontAwesomeIcon icon={item.icon} />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', lineHeight: 1 }}>
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            }
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          {/* Mobile AppBar */}
          <AppBar position="fixed" sx={{ background: MS_COLORS.background, color: MS_COLORS.text, boxShadow: '0 1px 0 rgba(0,0,0,0.06)', zIndex: 1300 }}>
            <Toolbar sx={{ minHeight: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
              <img src={YambaFullLogoPng} alt="Yamba Logo" style={{ height: '34px' }} />
              <IconButton onClick={toggleDrawer} sx={{ color: MS_COLORS.text, width: 36, height: 36 }}>
                <FontAwesomeIcon icon={faBars} size="sm" />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Mobile Drawer */}
          <Drawer
            variant="temporary"
            open={isDrawerOpen}
            onClose={toggleDrawer}
            ModalProps={{ keepMounted: true }}
            sx={{
              zIndex: 1400,
              '& .MuiDrawer-paper': {
                width: 250,
                boxSizing: 'border-box',
                backgroundColor: MS_COLORS.background,
              },
              '& .MuiBackdrop-root': { // Style the backdrop
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }
            }}
          >
            {/* Close Button within Mobile Drawer */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1}}>
              <IconButton onClick={toggleDrawer} sx={{ color: MS_COLORS.textSecondary }}>
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
            </Box>
            {drawerContent}
          </Drawer>
        </>
      ) : (
        /* Permanent Drawer for Desktop */
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              width: 270,
              boxSizing: 'border-box',
              backgroundColor: MS_COLORS.background,
              borderRight: `1px solid ${MS_COLORS.border}`, // Subtle right border
            }
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </>
  );
};

export default Navigation;